import Text from '@component/Text/Text'
import UserThumbnail from '@component/images/UserThumbnail'
import React, {useMemo} from 'react'
import {IUserRank} from '@api/rank/rank'
import useFormatNumber from '@hook/useFormatNumber'
import IconDown from '@svg/common/ic_btn_down.svg'
import IconUp from '@svg/common/ic_btn_up.svg'
import {useLoginStore} from '@store/LoginStore'
import {useRouter} from 'next/router'
import useWindowSize from '@hook/useWindowSize'
import ExchangeTag from '@component/tags/ExchangeTag'
import CopyableTag from '@component/tags/CopyableTag'
import useNavigation from '@hook/useNavigation'
import ProTag from '@component/tags/ProTag'
import useQueryGetExchanges from '@hook/query/core/exchange/useQueryGetExchanges'
import WizSimpleAreaChart from '@component/charts/WizAreaChart/WizSimpleAreaChart'
import WizIcon from '@component/images/WizIcon'
import classNames from 'classnames'
import {createLog} from '@util/logs'
import LeagueExpandedUserCell from '@feature/leaderboard/components/LeagueUserCell/LeagueExpandedUserCell'
import {useTranslation} from '@lib/intl/useTranslations'

interface Props extends IUserRank {
    isExpanded: boolean
    onPress: () => void
    kind: 'balance' | 'profit'
    dataCy?: string
    leagueId?: string
}

const LeagueUserCell = ({
    id,
    rank,
    name,
    score,
    active_exchanges,
    copy_exchanges,
    thumbnail,
    simple_graph,
    kind,
    isExpanded,
    onPress,
    is_paid_leader,
    dataCy,
    is_pro,
    badges,
    leagueId,
}: Props) => {
    const {displayBalance, displayPercent} = useFormatNumber()
    const router = useRouter()
    const {getExchange} = useQueryGetExchanges()
    const myID = useLoginStore(s => s.id)
    const t = useTranslation()
    const isCopiable = () => (copy_exchanges?.length ?? 0) > 0

    const {goUserPortfolio} = useNavigation()
    const {isMd} = useWindowSize()

    const onClickExpandIcon = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e?.cancelable) {
            e?.preventDefault()
        }
        e?.stopPropagation()
        onPress()
    }
    const onClickContainer = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isExpanded && !isMd) {
            onClickExpandIcon(e)
            return
        }
        goUserPortfolio(id)
        createLog('event', 'leaderboard_league_profile_click', {user_id: id, league_id: router?.query?.league})
    }

    const exchangeList = useMemo(() => {
        return active_exchanges?.sort()
    }, [active_exchanges])

    const exchangesStr = useMemo(
        () => active_exchanges?.map(a => getExchange(a)?.display_name).join(', ') || '',
        [active_exchanges, getExchange],
    )

    const profitColorClass = useMemo(() => {
        if (kind === 'balance') return 'text-gray01 dark:text-dark_gray01'
        if (score < 0) return 'text-red_shade dark:text-dark_red_shade'
        if (score > 0) return 'text-primary_shade dark:text-dark_primary_shade'
        return 'text-gray01 dark:text-dark_gray01'
    }, [kind, score])

    const scoreValue = useMemo(
        () => (kind === 'balance' ? displayBalance(score, {isUSD: true, showPreSign: true}) : displayPercent(score)),
        [score, displayBalance, kind, displayPercent],
    )

    const graphData = useMemo(() => {
        if (!simple_graph || simple_graph.length === 0) return []
        return simple_graph.map((g, i) => ({x: i + 1, y: g}))
    }, [simple_graph])

    const containerCellStyleLight = `${
        isExpanded
            ? myID === id
                ? 'ring-brown bg-brown_tint01'
                : 'ring-primary bg-primary_tint02 hover:bg-primary_tint02'
            : myID === id
            ? 'ring-transparent bg-brown_tint01 hover:bg-brown_tint01'
            : 'ring-transparent hover:bg-primary_tint02'
    }`
    const containerCellStyleDark = `${
        isExpanded
            ? myID === id
                ? 'dark:ring-dark_brown dark:bg-dark_brown_tint01 dark:hover:dark_brown_tint01'
                : 'dark:ring-dark_primary dark:bg-dark_primary_tint02 hover:dark:bg-dark_primary_tint02'
            : myID === id
            ? 'ring-transparent dark:bg-dark_brown_tint01 dark:hover:bg-dark_brown_tint01'
            : 'ring-transparent dark:hover:bg-dark_primary_tint02'
    }`

    return (
        <>
            <div
                data-cy={dataCy}
                className={classNames(
                    'rounded-md cursor-pointer select-none ring-[1px] py-[20px] px-[20px] mb-[1px] md:mt-[10px] md:py-[5px] md:pl-[0px] md:pr-[7px]',
                    containerCellStyleLight,
                    containerCellStyleDark,
                    isExpanded && 'mb-[1px] ',
                )}
                onClick={onClickContainer}>
                <div
                    key={rank}
                    className="items-center grid grid-cols-[50px_300px_auto_100px_162px] md:grid-cols-[30px_auto_100px] gap-[10px] md:gap-[5px]">
                    {/* Rank */}
                    <Text className={'text-center text-gray01 dark:text-dark_gray01 md:text-body2'}>{rank}</Text>
                    {/* Thumbnail, Name, Tags */}
                    <div className={'flex items-center overflow-hidden'}>
                        <UserThumbnail
                            src={thumbnail}
                            size={30}
                            containerClassName={'mr-[5px]  flex-none'}
                            imageClassName={'rounded-full'}
                            isPro={is_pro}
                        />
                        <div className={'truncate'}>
                            <div>
                                <div className={'flex items-center gap-x-[5px]'}>
                                    <Text
                                        className={'text-ti1 md:text-ti2 truncate text-gray01 dark:text-dark_gray01'}
                                        enablePreWhiteSpace={false}>
                                        {name}
                                    </Text>
                                    {is_pro && <ProTag size={'small'} />}
                                    {leagueId === '67e0d0ed99b3edb11140b438' && badges?.includes('blofin') ? (
                                        <div
                                            className={
                                                'md:hidden bg-[#FFF3E5] dark:bg-[#3B2718] py-[3px] pl-[3px] pr-[7px] rounded-[10px] flex items-center gap-x-[3px]'
                                            }>
                                            <ExchangeTag
                                                key={'blofin'}
                                                textClassName={'hidden'}
                                                iconSize={16}
                                                exchange={'blofin'}
                                                className={'rounded-full overflow-hidden'}
                                            />
                                            <Text
                                                className={
                                                    'text-[#9E5400] dark:text-[#E19B6E] text-body_comm font-bold whitespace-nowrap'
                                                }
                                                enablePreWhiteSpace={false}>
                                                {t('event.blofin1.rank.blofin.leader')}
                                            </Text>
                                        </div>
                                    ) : null}
                                </div>

                                <div className={'flex items-center space-x-[5px] md:hidden'}>
                                    {isCopiable() && <CopyableTag />}
                                </div>
                            </div>

                            <div className={'flex items-center gap-x-[5px]'}>
                                {isCopiable() && <CopyableTag className={'hidden md:block'} />}
                                {leagueId === '67e0d0ed99b3edb11140b438' && badges?.includes('blofin') ? (
                                    <div
                                        className={
                                            'hidden md:flex bg-[#FFF3E5] dark:bg-[#3B2718] py-[3px] pl-[3px] pr-[7px] rounded-[10px] items-center gap-x-[3px]'
                                        }>
                                        <ExchangeTag
                                            key={'blofin'}
                                            textClassName={'hidden'}
                                            iconSize={16}
                                            exchange={'blofin'}
                                            className={'rounded-full overflow-hidden'}
                                        />
                                        <Text
                                            className={
                                                'text-[#9E5400] dark:text-[#E19B6E] text-body_comm font-bold whitespace-nowrap'
                                            }
                                            enablePreWhiteSpace={false}>
                                            {t('event.blofin1.rank.blofin.leader')}
                                        </Text>
                                    </div>
                                ) : null}
                                <Text
                                    className={'text-body3 text-gray03 dark:text-dark_gray03 hidden md:block truncate'}
                                    enablePreWhiteSpace={false}>
                                    {exchangesStr}
                                </Text>
                            </div>
                        </div>
                    </div>

                    {/* Exchanges */}
                    <div className={'flex items-center md:hidden flex-nowrap gap-[10px]'}>
                        {exchangeList?.length > 2 ? (
                            <>
                                {exchangeList?.slice(0, 2)?.map(exchangeName => (
                                    <ExchangeTag
                                        key={exchangeName}
                                        iconSize={20}
                                        textClassName={'text-body1'}
                                        exchange={exchangeName}
                                    />
                                ))}
                                <Text className={'text-body2 text-gray01 dark:text-dark_gray01'}>
                                    +{exchangeList?.length - 2}
                                </Text>
                            </>
                        ) : (
                            exchangeList?.map(exchangeName => (
                                <ExchangeTag
                                    key={exchangeName}
                                    iconSize={20}
                                    textClassName={'text-body1'}
                                    exchange={exchangeName}
                                />
                            ))
                        )}
                    </div>

                    {/* Score */}
                    <div className={'flex flex-col items-end gap-y-[5px]'}>
                        <Text className={`text-right md:text-body2 ${profitColorClass}`}>{scoreValue}</Text>
                    </div>

                    {/* Chart, Expand */}
                    <div className={'flex items-center justify-between gap-[10px] md:hidden'}>
                        <div className={'min-w-[100px] px-[10px]'}>
                            <WizSimpleAreaChart data={graphData} width={100} height={34} className={'mt-0'} />
                        </div>
                        <div className={''} onClick={onClickExpandIcon}>
                            {isExpanded ? (
                                <WizIcon
                                    backgroundColorClassName={
                                        'p-[7px] hover:bg-transparent01 dark:hover:bg-dark_transparent01'
                                    }>
                                    <IconUp className={'w-[18px] fill-gray01 dark:fill-dark_gray01'} />
                                </WizIcon>
                            ) : (
                                <WizIcon
                                    backgroundColorClassName={
                                        'p-[7px] hover:bg-transparent01 dark:hover:bg-dark_transparent01'
                                    }>
                                    <IconDown className={'w-[18px] fill-gray01 dark:fill-dark_gray01'} />
                                </WizIcon>
                            )}
                        </div>
                    </div>
                </div>
                {isExpanded && <LeagueExpandedUserCell id={id} isExpanded={isExpanded} />}
            </div>
        </>
    )
}

export default React.memo(LeagueUserCell)
